/**
 * BRAND CATEGORIES
 * 
 * Types and utilities for categorizing polish brands as:
 * - Indie: Small/independent makers
 * - Boutique: Small-batch but professional brands
 * - Mainstream: Big box store brands
 * 
 * Brand lists curated from real collector data (thank you Blu!)
 */

export type BrandCategory = 'indie' | 'boutique' | 'mainstream' | 'unknown';

export interface BrandCategoryInfo {
  brand: string;
  category: BrandCategory;
  updatedAt?: Date;
}

export const BRAND_CATEGORY_OPTIONS = [
  { value: 'indie', label: 'Indie', emoji: '🎨', color: 'bg-purple-600 text-white border border-purple-700', description: 'Small/independent makers' },
  { value: 'boutique', label: 'Boutique', emoji: '✨', color: 'bg-pink-600 text-white border border-pink-700', description: 'Small-batch professional brands' },
  { value: 'mainstream', label: 'Mainstream', emoji: '🏪', color: 'bg-blue-600 text-white border border-blue-700', description: 'Big box store brands' },
] as const;

// Known indie brands (small/independent makers, often one-person operations)
// These are brands commonly found at PPU, HHC, and other indie events
export const KNOWN_INDIE_BRANDS = [
  // Numbers
  '1143 H₂O', '1143 H2O', '1422 Designs', '6 Harts', '77 Nail Lacquer',
  // A
  'A England', 'A. England', 'Ahmya Cosmetics', 'Alchemy Lacquers', 'Aliquid Lacquer',
  'All Mixed Up Lacquers', 'Alter Ego', 'Anchor & Heart Lacquer', 'Anonymous Lacquer',
  'Arcane Lacquer', 'Atomic Polish',
  // B
  'B² Lacquer', 'Baroness X', 'BCB Lacquers', 'Bear Pawlish', 'Beaux Rêves Lacquer',
  'Bedford & Grove', 'Bee\'s Knees Lacquer', 'Bent Mind Designs', 'Black Dahlia Lacquer',
  'Blue-Eyed Girl Lacquer', 'Bluebird Lacquer', 'Blush Lacquers', 'Braves Diva Polish',
  'Bright & Polished', 'by Dany Vianna',
  // C
  'Cadillacquer', 'Cameo Colours Lacquers', 'Carpe Noctem Cosmetics', 'Celestial Cosmetics',
  'Chaos & Crocodiles', 'Charlie\'s Rad Lacquer', 'Chirality Artisan Nail Polish',
  'Chirality Nail Polish', 'Clionadh Cosmetics Nail Lacquer', 'Colores de Carol',
  'Contrary Polish', 'Crow & Opal Nail Lacquer', 'CrowsToes Nail Color', 'Cupcake Polish',
  'Cuticula Nail Lacquer', 'Colors by Llarowe',
  // D
  'Dam Nail Polish', 'Dance Legend', 'Danglefoot Polish', 'Danglefoot Nail Polish',
  'Dark & Twisted Lacquer', 'Dashing Diva', 'Daveen Lacquer', 'Death Valley Nails',
  'Delush Polish', 'Different Dimension', 'Doctor Lacquer', 'Dollish Polish',
  'Dreamland Artisan Nail Lacquer', 'Dreamland Lacquer', 'Drip Drop Nail Paint',
  'Drunk Fairy Polish',
  // E
  'El Corazon', 'ellagee', 'Elevation Polish', 'Emerald & Ash', 'Emily de Molly',
  'Enchanted Polish', 'Envy Lacquer', 'Ethereal Lacquer', 'Ever After Polish',
  // F
  'F.U.N Lacquer', 'F.U.N. Lacquer', 'Fair Maiden', 'Fair Maiden Polish', 'Fancy Gloss',
  'Femme Fatale', 'Frenzy Polish',
  // G
  'Garden Path Lacquers', 'Geeky Bee\'s Nail Colors', 'Ginger Polish', 'Girly Bits',
  'Glam Polish', 'Glisten & Glow', 'Grace-full Nail Polish', 'Great Lakes Lacquer',
  // H
  'Hare Polish', 'Heather\'s Hues', 'Hearts & Promises',
  // I
  'Illyrian Polish', 'Indie Lacquer', 'Indie Polish by Patty Lopes',
  // J
  'Jen & Berries handcrafted lacquer', 'Jilly Bean Indie Polish', 'Jior Couture',
  'Joy Lacquer', 'JReine', 'Jreine Cosmetics', 'J. Reine',
  // K
  'Kathleen & Co', 'Kathleen & Co.', 'KB Shimmer Nail Polish', 'KBShimmer',
  'Kissing Toad Lacquer',
  // L
  'le Polish', 'Leesha\'s Lacquer', 'Lemming Lacquer', 'Literary Lacquers',
  'Living Dead Lacquer', 'Loaded Lacquer', 'Lollipop Posse Lacquer', 'Lou it Yourself',
  'Lou It Yourself', 'Lucky 13 Lacquer', 'Lumen', 'Lurid Lacquer', 'Lyn B Designs',
  'Lynnderella',
  // M
  'M&N Indie Polish', 'ManiScript', 'Manitude', 'Mango Bunny Polish', 'Masura',
  'Mckfresh Nail Attire', 'MckFresh Nail Attire', 'Model City Polish', 'Monarch Lacquer',
  'Moon Shine Mani', 'Moonflower Polish', 'Ms. Sparkle', 'My Indie Polish', 'My Ten Friends',
  'Mystic Muse Nail Lacquer',
  // N
  'Nail Hoot', 'Nail Hoot Indie Lacquers', 'Native War Paints', 'Necessary Evil Polish',
  'Nerd Lacquer', 'Nevermind Polish', 'Night Owl Lacquer', 'Ninja Polish',
  'Noodles Nail Polish', 'Northern Star', 'Nvr Enuff Polish',
  // O
  'Octopus Party Nail Lacquer',
  // P
  'P•O•P Nail Polish', 'Pahlish', 'Paint It Pretty Polish', 'Painted Polish',
  'Pampered Polishes', 'Paradox Polish', 'Parrot Polish', 'Penelope Luz Cosmetics',
  'Pepper Polish', 'Philly Loves Lacquer', 'Phoenix Indie Polish', 'Pi Colors',
  'Picture Polish', 'Pipe Dream Polish', 'Poetry Cowgirl Nail Polish', 'Polish \'M',
  'Polish Me Silly', 'Polished for Days', 'Polished Pretties', 'Potion Polish',
  'Powder Perfect', 'Pretty Jelly', 'Pretty Serious Cosmetics', 'Princessly Polished',
  'Prism Polish UK', 'Pug Posse Nail Lacquer',
  // Q
  'Quixotic Polish',
  // R
  'Red Eyed Lacquer', 'Rogue Lacquer',
  // S
  'Sassy Cats Lacquer', 'Sassy Sauce Polish', 'Savannah Lacquer', 'Sayuri Nail Lacquer',
  'Scofflaw Nail Varnish', 'Serum No. 5', 'Shleee Polish', 'Siren of the Sea',
  'Smitten Polish', 'Smokey Mountain Lacquers', 'Southern Lands Lacquer', 'Spell Polish',
  'Spellbound Nails', 'Stella Chroma', 'Studio M', 'Super Moon Lacquer', 'SuperChic Lacquer',
  'Supernatural Lacquer', 'Swamp Gloss Indie Polish', 'Sweet & Sour Lacquer',
  'Sweet Heart Polish',
  // T
  'Takko Lacquer', 'takko lacquer', 'The Don Deeva', 'The Lady Varnishes',
  'Tonic Polish', 'Turtle Tootsie Polishes', 'Tux Polish', 'Twinkled T', 'twinkled t',
  'Twisting Nether Lacquer',
  // U
  'Under A Hula Moon',
  // V
  'Vanessa Molina Nail Polish', 'Vapid Lacquer', 'Vapid Nail Lacquer',
  // W
  'What\'s Up Nails', 'whatcha indie polish', 'Wildflower Lacquer', 'Windestine',
  'Witchcult Nail Lacquer',
  // Z
  'Zaftig Lacquer', 'Zombie Claw Polish',
  
  // Additional well-known indies from uncategorized list
  '\'Ard as Nails', 'Bow Nail Polish by Dance Legend', 'Lilypad Lacquer', 'Starrily',
  'Top Shelf Lacquer', 'Indigo Bananas', 'Liquid Sky Lacquer', 'Darling Diva Polish',
  'Beyond the Nail', 'Digital Nails', 'Cult Nails', 'Rescue Beauty Lounge',
  'Nail Pattern Boldness', 'Jindie Nails', 'SquareHue', 'Bohemian Polish',
  'Glitter Gal', 'Apipila', 'Lacquester', 'Love, Angeline', 'Polishaus',
  'Luna Lacquer', 'Crystal Knockout', 'Hypnotic Polish', 'Aurora Lacquers',
  'Honey Bunny Lacquer', 'Virago Varnish', 'Daily Hues Nail Lacquer',
  'Dainty Digits Lacquer', 'Dainty Digits Polish', 'Color Spectrum Polish',
  'Glitter Daze', 'Pretty & Polished', 'Pretty Beautiful Unlimited',
  'Fiendish Fancies', 'Elixir Lacquers', 'Ruby White Tips', 'Peachtree Polish',
  'Starbeam Polish', 'Treo Lacquer', 'Radioactive Unicorn', 'Petal Polish',
  'NailNation 3000', 'Pixel Polish', 'Gridlock Lacquer', 'Lavish Polish',
  'My Darling Curios', 'Foxy Paws', 'Stardust Polish', 'Luxe Lacquers',
  '24 Karat Lacquer', 'Wrenn Polish', 'Wicked Lacquer', 'Aurora Nail Lacquer',
  'Red Panda Lacquer', 'Black Label Nails', 'Naps and Nails', 'Fairytale Finish',
  'Holovers Indie Polish', 'Lucky Lacquer', 'Starlight Polish', 'Super Black Lacquer',
  'Space Time Travels', 'Paint Box Polish', 'Piper Polish', 'Posh Paints Lacquer',
  'Krisable Designs', 'GLH Nail Lacquers', 'Nine Zero', 'Mace Polish',
  'MJ Lacquer', 'Adored Colors', 'JoHa\'s Love Nail Polish', 'Love Lacquer',
  'Polished and Pretty by MBA', 'Fedora Harp', 'Lily Anna',
];

// Known boutique brands (small-batch professional brands, often creator-owned businesses with wider distribution)
export const KNOWN_BOUTIQUE_BRANDS = [
  'ILNP', 'Holo Taco', 'Live Love Polish', 'mooncat', 'mooncat Nail Lacquer',
  'Cirque Colors', 'illimite', 'NCLA', 'Floss Gloss', 'Lights Lacquer',
  'Orosa', 'KL Polish', 'Olive & June',
];

// Known mainstream brands (big box store brands, widely distributed)
export const KNOWN_MAINSTREAM_BRANDS = [
  // Major salon/drugstore brands
  'OPI', 'O•P•I', 'O•P•I Nail Lacquer', 'Essie', 'essie', 'essie nail lacquer',
  'Sally Hansen', 'China Glaze', 'Orly', 'Zoya', 'CND', 'Color Club',
  'Morgan Taylor', 'Morgan Taylor Professional Nail Lacquer',
  // Drugstore brands
  'Revlon', 'Maybelline', 'L\'Oréal', 'L\'Oreal', 'Sinful Colors', 'Wet N Wild',
  'Wet n Wild', 'Hard Candy', 'Milani', 'Milani Nail Lacquer', 'Pure Ice',
  'Rimmel', 'CoverGirl', 'Covergirl', 'NYC', 'L.A. Colors', 'LA Colors',
  'Catrice', 'essence', 'Elf Nail Polish',
  // Store brands
  'Sally Girl', 'Claire\'s', 'Ulta', 'Sephora by O•P•I Nail Color',
  'Sephora X', 'Formula X Nail Color', 'Five Below',
  // Other mainstream
  'FingerPaints', 'Salon Perfect', 'Nicole by O•P•I', 'EverGlaze by China Glaze',
  'Kleancolor', 'Piggy Polish', 'Fresh Paint', 'Fing\'rs', 'Icing', 'Color Mates',
  'Funky Fingers', 'Pop•Arazzi', 'Max Factor', 'Jordana', 'Crayola',
  'Disney Polish', 'Flower Nail\'d It Nail Lacquer', 'Bubble Yum',
  'Kardashian Beauty', 'Love My Nails', 'Cinapro Nail Creations',
  'Petites by Scherer', 'Chameleon by Scherer', 'Chameleon Nails', 'Panama Jack',
  'Marc Jacobs', 'Dior', 'Christian Dior',
  // UK/International mainstream
  'Barry M', 'Barry M.', 'Nails Inc', 'Nails, Inc. London', 'Models Own', 'Model\'s Own',
  // Premium mainstream
  'Butter London', 'Deborah Lippmann', 'JINsoon', 'Smith & Cult', 'Londontown',
  'Julep', 'Julep Nail Color',
  // Gel/professional
  'Gelish', 'Kiara Sky', 'LeChat', 'Le Chat Nail Lacquer', 'DND', 'Red Carpet Manicure',
  'Sensationail', 'Beetles', 'Modelones',
  // Stamping mainstream
  'Maniology', 'UberChic', 'Born Pretty', 'BeautyBigBang', 'Beauty Big Bang',
  'MoYou London', 'Moyou London', 'Bundle Monster', 'Pueen', 'Winstonia', 'konad',
  // Top/base coats
  'Seche', 'Out the Door', 'Poshe', 'INM', 'Nail Tek', 'Nailtiques', 'Duri', 'Barielle',
  'Dazzle Dry', 'Nina Ultra Pro',
  // Other
  'Avon', 'Bonita Salon', 'Broadway Nails', 'Kiss', 'Jesse\'s Girl',
  'NanaCoco', 'Santee', 'Confetti', 'BonBons', 'Urban Outfitters', 'Urban Decay',
  'American Apparel', 'Bongo', 'Material Girl', 'Blackheart Beauty',
  'Bath & Body Works', 'Sonia Kashuk Nail Colour', 'Saki Cosmetics',
  'Ella + Mila', 'Spa Ritual', 'Dermelect', 'Borghese', 'Illamasqua',
  'Etude House', 'Anna Sui', 'Layla Nail Polish', 'Golden Rose',
];

// Get category display info
export function getCategoryDisplay(category: BrandCategory) {
  return BRAND_CATEGORY_OPTIONS.find(c => c.value === category) || {
    value: 'unknown',
    label: 'Unknown',
    emoji: '❓',
    color: 'bg-slate-800/40 text-glass-secondary',
    description: 'Not yet categorized'
  };
}

// Check if a brand matches a known brand (case-insensitive)
export function findKnownCategory(brand: string): BrandCategory | null {
  const normalizedBrand = brand.toLowerCase().trim();
  
  if (KNOWN_INDIE_BRANDS.some(b => b.toLowerCase() === normalizedBrand)) {
    return 'indie';
  }
  if (KNOWN_BOUTIQUE_BRANDS.some(b => b.toLowerCase() === normalizedBrand)) {
    return 'boutique';
  }
  if (KNOWN_MAINSTREAM_BRANDS.some(b => b.toLowerCase() === normalizedBrand)) {
    return 'mainstream';
  }
  
  return null;
}
